import * as React from "react";
import { inject } from "mobx-react";
import { Viewport, ViewMedium } from "bernie-view";
import { decorateDestinationHeroHtml, Sources } from "../DestinationHeroUtilities";
import { WizardHeroWithHeadingsProps } from "../typings";
import { Media } from "typings/microserviceModels/destination-hero-flex-module";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkFigure } from "uitk-react-images";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";

export const WizardHeroWithHeadings = inject(
  "wizardState",
  "compositionStore"
)((props: WizardHeroWithHeadingsProps) => {
  const { templateComponent, model, wizardState, compositionStore } = props;

  if (!templateComponent) {
    return null;
  }
  const {
    metadata: { id },
  } = templateComponent;
  const { content } = model;
  const { formatText } = useLocalization();

  const heading = compositionStore?.pageHeading;
  const subHeading = compositionStore?.pageSubHeadline;
  const hasHeadings = heading || subHeading;

  const firstImage = content && content.media && content.media[0];
  const fallbackImage: Media = {
    // Empty fields are required to adhere to our autogenerated typing for Media.
    cardIdentifier: "",
    mediaUrl: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaUrlTrimmed: "",
    mediaUrlOriginal: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaAlt: "Default HTG hotel image",
    origin: "Expedia",
    source: "",
    author: "",
    authorUrl: "",
    license: { name: "", suite: "", url: "" },
    mediaType: "Image",
  };
  const selectedHeroImage =
    firstImage && !firstImage.mediaUrl.includes("TE_Clouds_card_r1.jpg") ? firstImage : fallbackImage;

  const { mediaUrl } = selectedHeroImage;
  if (wizardState) {
    wizardState.globalWizardState.config.heroImageSrc = mediaUrl;
  }

  const { source } = selectedHeroImage;
  const isTroverSource = source === Sources.Trover;
  const isWikimediaSource = source === Sources.Wikimedia;
  const isAttributionRequired = isTroverSource || isWikimediaSource;

  const heroTitle = (): string => {
    if (!isWikimediaSource) {
      return "";
    }

    return formatText(
      "hero.attribution.wikimediaAlt",
      selectedHeroImage.mediaAlt,
      selectedHeroImage.author,
      selectedHeroImage.license && selectedHeroImage.license.name
    );
  };

  return (
    <div className="WizardHeroWithHeadings" id={id}>
      <UitkFigure className="heroImageFigure">
        <BlossomImage alt={heroTitle()} src={mediaUrl} />
        <Viewport>
          <ViewMedium>
            <>
              {hasHeadings && (
                <UitkScrim type={UitkScrimType.BOTTOM}>
                  <UitkSpacing padding={{ inline: "six", block: "six" }}>
                    <UitkLayoutPosition type="absolute" position={{ bottom: "zero" }}>
                      <div className="heroImageHeadings">
                        {heading && (
                          <UitkHeading tag="h1" size={2}>
                            {heading}
                          </UitkHeading>
                        )}
                        {subHeading && (
                          <UitkSpacing padding={{ blockstart: "two" }}>
                            <UitkHeading tag="h2" size={4}>
                              {subHeading}
                            </UitkHeading>
                          </UitkSpacing>
                        )}
                      </div>
                    </UitkLayoutPosition>
                  </UitkSpacing>
                </UitkScrim>
              )}
            </>
          </ViewMedium>
        </Viewport>
      </UitkFigure>
      <AttributionText
        isAttributionRequired={isAttributionRequired}
        selectedHeroImage={selectedHeroImage}
        isTroverSource={isTroverSource}
      />
    </div>
  );
});

interface AttributionTextProps {
  isAttributionRequired: boolean;
  selectedHeroImage: Media;
  isTroverSource: boolean;
}

const AttributionText: React.FC<AttributionTextProps> = ({
  isAttributionRequired,
  selectedHeroImage,
  isTroverSource,
}): React.ReactElement | null => {
  const { formatText } = useLocalization();

  if (!isAttributionRequired) {
    return null;
  }

  const { mediaUrlOriginal, author, authorUrl, license } = selectedHeroImage;
  const licenseSuite = license && license.suite;
  const licenseUrl = license && license.url;

  const htmlToDisplay = () => {
    if (isTroverSource) {
      const troverPhotoAnchor = formatText("hero.attribution.troverLink", author, mediaUrlOriginal);

      return decorateDestinationHeroHtml(troverPhotoAnchor);
    }
    const wikimediaPhotoAnchor = formatText(
      "hero.attribution.wikimediaLink",
      mediaUrlOriginal,
      authorUrl,
      author,
      licenseUrl,
      licenseSuite
    );

    return decorateDestinationHeroHtml(wikimediaPhotoAnchor);
  };

  return (
    <UitkSpacing padding={{ inline: "three", block: "two" }}>
      <span className="uitk-type-200" dangerouslySetInnerHTML={htmlToDisplay()} />
    </UitkSpacing>
  );
};
